import apiClient, { getApiErrorInfo } from './api';

export interface CpeRequirementImportStatus {
  stateCode: string;
  stateName: string;
  lastScrapedAt: string | null;
  sourceLastUpdated: string | null;
  lastParsedAt: string | null;
  parsingModel: string | null;
  lastChangeAt: string | null;
  lastChangeType: string | null;
  nasbaUrl: string | null;
  /** When a human last verified this jurisdiction's requirement data (null = never). */
  verifiedAt: string | null;
  /** Admin display name recorded at verification time. */
  verifiedBy: string | null;
}

export interface CpeRequirementVerification {
  stateCode: string;
  verifiedAt: string | null;
  verifiedBy: string | null;
}

export interface StateSubjectRequirement {
  id: number;
  stateCode: string;
  subjectArea: string;
  requiredHours: number;
  notes: string | null;
}

export interface StateCreditLimitation {
  id: number;
  stateCode: string;
  limitationType: string;
  limitationDescription: string;
  maxHours: number | null;
  maxPercentage: number | null;
}

export interface StateCreditCalculationRule {
  id: number;
  stateCode: string;
  activityType: string;
  calculationRule: string;
  minimumHours: number | null;
  incrementHours: number | null;
  notes: string | null;
}

export interface StatePolicy {
  id: number;
  stateCode: string;
  policyType: string;
  policyDescription: string;
}

/**
 * Editable requirement field values (full-replace: every field is sent on
 * save; nulls clear the value). Dates are ISO strings (yyyy-MM-dd accepted).
 */
export interface UpdateCpeRequirementFields {
  licenseRenewalDate: string | null;
  cpeReportingPeriod: string | null;
  generalRequirementHours: number | null;
  ethicsRequirementHours: number | null;
  reportingPeriodYears: number;
  minimumHoursPerYear: number | null;
  reportingPeriodType: string;
  birthMonthAlignment: string | null;
  periodCalculationNotes: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  dateOfLastRevision: string | null;
  otherSubjectAreaRequirements: string | null;
  creditLimitationsText: string | null;
  creditCalculationText: string | null;
  otherPoliciesText: string | null;
  /**
   * Reciprocity + carryover (engine-driving values). Unlike other fields,
   * null/omitted on save means "leave unchanged" rather than "clear".
   * carryoverMaxHours is sent as a string ('' = clear) so the server can
   * validate tenths precision exactly.
   */
  allowsReciprocity: boolean | null;
  reciprocityEthicsCarveout: boolean | null;
  reciprocityNotesText: string | null;
  carryoverMaxHours: string | null;
}

export interface StateRequirementConstraintSummary {
  id: string;
  constraintScope: string;
  requiredHours: number | null;
  minimumHours: number | null;
  maximumHours: number | null;
  frequencyMonths: number | null;
  appliesToNewLicensesOnly: boolean;
  description: string;
}

/** Flexible-model category + constraints — the source compliance actually consumes. */
export interface StateRequirementCategorySummary {
  id: string;
  categoryName: string;
  categoryType: string;
  targetKind: string;
  isConditional: boolean;
  description: string | null;
  constraints: StateRequirementConstraintSummary[];
}

export interface CpeRequirementParsedDetail {
  totalHoursRequired: number | null;
  reportingPeriodMonths: number | null;
  ethicsHoursRequired: number | null;
  requirementsJson: string | null;
  carryoverRules: string | null;
  notes: string | null;
  sourceLastUpdated: string | null;
  lastParsedAt: string | null;
  parsingModel: string | null;
  updatedAt: string;
}

export interface CpeRequirementLastChange {
  changeAt: string;
  changeType: string;
  changedFields: string[];
  processedBy: string | null;
  parsingModel: string | null;
  notes: string | null;
}

/** Badge count + liveness for the external NASBA requirements-monitor agent. */
export interface MonitorStatus {
  unreviewedCount: number;
  /** When the monitor agent last reported in (any run, even no-change). Null = never. */
  lastRunAt: string | null;
}

/** Requirement change detected by the external NASBA requirements-monitor agent. */
export interface MonitorFinding {
  id: string;
  stateCode: string;
  summary: string | null;
  changedFields: string[];
  sourceUrl: string | null;
  sourceLastUpdated: string | null;
  detectedAt: string;
  parsingModel: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export interface CpeRequirementImportDetail {
  stateCode: string;
  stateName: string;
  nasbaUrl: string | null;
  stateBoardUrl: string | null;
  lastScrapedAt: string | null;
  sourceLastUpdated: string | null;
  dateOfLastRevision: string | null;
  createdAt: string;
  updatedAt: string;
  verifiedAt: string | null;
  verifiedBy: string | null;
  licenseRenewalDate: string | null;
  cpeReportingPeriod: string | null;
  generalRequirementHours: number | null;
  ethicsRequirementHours: number | null;
  reportingPeriodYears: number;
  minimumHoursPerYear: number | null;
  reportingPeriodType: string;
  birthMonthAlignment: string | null;
  periodCalculationNotes: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  otherSubjectAreaRequirements: string | null;
  creditLimitationsText: string | null;
  creditCalculationText: string | null;
  otherPoliciesText: string | null;
  allowsReciprocity: boolean;
  reciprocityEthicsCarveout: boolean;
  reciprocityNotesText: string | null;
  carryoverMaxHours: number | null;
  subjectRequirements: StateSubjectRequirement[];
  creditLimitations: StateCreditLimitation[];
  calculationRules: StateCreditCalculationRule[];
  policies: StatePolicy[];
  requirementCategories: StateRequirementCategorySummary[];
  parsedDetail: CpeRequirementParsedDetail | null;
  lastChange: CpeRequirementLastChange | null;
}

class CpeRequirementImportService {
  async getImportStatus(): Promise<CpeRequirementImportStatus[]> {
    try {
      const response = await apiClient.get<CpeRequirementImportStatus[]>(
        '/api/cpe-requirements/admin/import-status',
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load CPE requirements import status', { cause: error });
    }
  }

  /** Mark (verified=true) or clear (verified=false) the human-verification flag for a jurisdiction. */
  async setVerification(stateCode: string, verified: boolean): Promise<CpeRequirementVerification> {
    const url = `/api/cpe-requirements/admin/import-status/${encodeURIComponent(stateCode)}/verify`;
    try {
      const response = verified
        ? await apiClient.post<CpeRequirementVerification>(url)
        : await apiClient.delete<CpeRequirementVerification>(url);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to update verification status', { cause: error });
    }
  }

  /** Save edited requirement field values; returns the refreshed detail. */
  async updateImportDetail(
    stateCode: string,
    fields: UpdateCpeRequirementFields,
  ): Promise<CpeRequirementImportDetail> {
    try {
      const response = await apiClient.put<CpeRequirementImportDetail>(
        `/api/cpe-requirements/admin/import-status/${encodeURIComponent(stateCode)}`,
        fields,
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to save CPE requirement fields', { cause: error });
    }
  }

  /** Requirement changes detected by the external NASBA requirements monitor. */
  async getMonitorFindings(unreviewedOnly = true): Promise<MonitorFinding[]> {
    try {
      const response = await apiClient.get<MonitorFinding[]>(
        '/api/cpe-requirements/admin/monitor-findings',
        { params: { unreviewedOnly } },
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load monitor findings', { cause: error });
    }
  }

  /** Unreviewed-finding count (drives the admin badge) + when the agent last reported in. */
  async getMonitorStatus(): Promise<MonitorStatus> {
    try {
      const response = await apiClient.get<MonitorStatus>(
        '/api/cpe-requirements/admin/monitor-findings/count',
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load monitor status', { cause: error });
    }
  }

  /** Mark a monitor finding reviewed by the current admin. */
  async markMonitorFindingReviewed(id: string): Promise<MonitorFinding> {
    try {
      const response = await apiClient.post<MonitorFinding>(
        `/api/cpe-requirements/admin/monitor-findings/${encodeURIComponent(id)}/review`,
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to mark finding reviewed', { cause: error });
    }
  }

  async getImportDetail(stateCode: string): Promise<CpeRequirementImportDetail> {
    try {
      const response = await apiClient.get<CpeRequirementImportDetail>(
        `/api/cpe-requirements/admin/import-status/${encodeURIComponent(stateCode)}`,
      );
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load CPE requirements import detail', { cause: error });
    }
  }
}

export default new CpeRequirementImportService();
